import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navigation from '../components/shared/Navigation';
import Footer from '../components/shared/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const pollPaymentStatus = async (sessionId, attempts = 0) => {
      const maxAttempts = 5;
      
      if (attempts >= maxAttempts) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`);
        const data = await response.json();

        if (data.payment_status === 'paid') {
          setPaymentData(data);
          setLoading(false);
        } else {
          setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
        }
      } catch (error) {
        console.error(error);
        setLoading(false);
      }
    };

    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  return (
    <div>
      <Navigation />
      
      <div className="container section-spacing max-w-2xl">
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle data-testid="payment-success-title" className="text-3xl font-[var(--font-display)]">
              Payment Successful!
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">Verifying payment...</p>
              </div>
            ) : paymentData ? (
              <div className="space-y-6">
                <div className="bg-muted p-6 rounded-lg space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Payment Type:</span>
                    <span className="font-medium">
                      {paymentData.payment_type === 'deposit' ? 'Rental Hold Deposit' : 'Application Fee'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Amount:</span>
                    <span className="font-medium text-xl text-[#A06A3B]">
                      ${paymentData.amount?.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status:</span>
                    <span className="font-medium text-green-600">✓ PAID</span>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">What's Next?</h3>
                  <ul className="text-sm space-y-2 text-muted-foreground">
                    <li>• You'll receive a confirmation email shortly</li>
                    <li>• Our team will review your {paymentData.payment_type === 'deposit' ? 'reservation' : 'application'}</li>
                    <li>• We'll contact you within 24-48 hours</li>
                  </ul>
                </div>

                <div className="flex gap-4">
                  <Link to="/units" className="flex-1">
                    <Button variant="outline" className="w-full rounded-[var(--btn-radius)]">
                      Browse More Units
                    </Button>
                  </Link>
                  <Link to="/" className="flex-1">
                    <Button className="w-full bg-[#121416] text-[#FBF8F2] hover:bg-[#1B1F24] rounded-[var(--btn-radius)]">
                      Return Home
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">Payment details unavailable</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Footer />
    </div>
  );
}